import React, { useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import useDraftsQueue from '../hooks/useDraftsQueue';
import { colors } from '../styles/styles';
import ThoughtDetail from './ThoughtDetail';
import PrimaryButton from './common/PrimaryButton';
import DraftsExhaustedView from './DraftsExhaustedView';
import { clearAiSuggestionsCache } from '../utils/aiSuggestionsCache';
import { prefetchTagSuggestionsForThought } from '../services/aiSuggestionsPrefetch';

const isTextField = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable === true;
};

const isInsideNoSwipe = (target) => {
  if (!target || !target.closest) return false;
  return Boolean(
    target.closest(
      'input, textarea, [role=slider], [contenteditable=true], [data-no-swipe]'
    )
  );
};

const DraftsReview = ({ thoughts, allTags, onSave, updateThoughtLocally, onExit, deleteThought }) => {
  const queue = useDraftsQueue(thoughts);
  const [exhausted, setExhausted] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;

  // Tracks the currently-active card id so the prefetch cleanup can tell
  // "user advanced onto the prefetched card" (keep the cache entry) from
  // "user skipped past it" (abort to save tokens). Without this, the
  // cleanup ran abortTagSuggestionsForId at the exact moment the user
  // landed on the prefetched card, killing the warm cache and forcing a
  // redundant fresh fetch on every advance.
  const activeIdRef = useRef(null);
  activeIdRef.current = queue.activeThought ? queue.activeThought.id : null;

  // Auto-transition to the celebration view once the queue empties so the
  // user sees their work register before going back to the list.
  useEffect(() => {
    if (queue.isExhausted) setExhausted(true);
  }, [queue.isExhausted]);

  const exitedRef = useRef(false);
  const callOnExit = () => {
    if (exitedRef.current) return;
    exitedRef.current = true;
    onExitRef.current && onExitRef.current();
  };

  // Programmatic exit (button / keyboard / dismiss): rewind the pushed
  // history entry AND notify the parent. Both paths are idempotent via
  // exitedRef, so popstate firing later from the back() call is a no-op.
  const exit = () => {
    if (typeof window !== 'undefined' && window.history) {
      window.history.back();
    }
    callOnExit();
  };

  // Push a history entry on mount so browser back exits cleanly. Any
  // popstate while we're mounted is treated as exit — there are no
  // sub-flows that push deeper from within the review.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.history) return;
    window.history.pushState(null, '');
    const onPop = () => callOnExit();
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abort any prefetches still in flight when the surface unmounts.
  useEffect(() => () => clearAiSuggestionsCache(), []);

  const handleSkip = () => {
    if (queue.isExhausted) return;
    queue.skip();
  };
  const handleDone = () => {
    if (queue.isExhausted || !queue.canMarkDone) return;
    queue.markDone();
  };

  // Keyboard shortcuts. Suppressed when a text input owns focus.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (isTextField(document.activeElement)) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handleSkip();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleDone();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        exit();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        setShowDeleteConfirm(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // handlers read fresh state via the queue closure; rebinding per render
    // is the cheaper option vs. ref-juggling.
  });

  // Prefetch the upcoming card's AI tag suggestions so they're warm by
  // the time the user advances. The returned cleanup aborts the
  // in-flight request when the user skips PAST the upcoming card without
  // landing on it — without that, rapid swipes leave a trail of stale
  // concurrent fetches. activeIdRef lets the cleanup keep the entry
  // when the user actually lands on the prefetched card.
  useEffect(() => {
    return prefetchTagSuggestionsForThought(
      queue.nextThought,
      allTags,
      activeIdRef
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.nextThought && queue.nextThought.id, allTags]);

  // Swipe — left = skip, right = done. Ignores swipes that originate on
  // form controls (importance slider, tag input, comments textarea).
  const swipeHandlers = useSwipeable({
    onSwipedLeft: (e) => {
      if (isInsideNoSwipe(e?.event?.target)) return;
      handleSkip();
    },
    onSwipedRight: (e) => {
      if (isInsideNoSwipe(e?.event?.target)) return;
      handleDone();
    },
    delta: 50,
    trackMouse: false,
    preventScrollOnSwipe: false,
  });

  if (exhausted) {
    return <DraftsExhaustedView total={queue.total} onDismiss={exit} />;
  }

  const { activeThought, total, remaining, index, canMarkDone } = queue;

  return (
    <div
      {...swipeHandlers}
      style={{
        // Fill the viewport so the action bar can pin to the bottom edge
        // regardless of how tall the card content gets. dvh handles iOS
        // mobile chrome correctly; vh fallback for older browsers.
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: colors.background,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      {/* Header: back arrow (left) + centered counter + a11y announcement.
          Three columns of equal width so the counter stays visually centered
          even though the back button is asymmetric on the left. */}
      <div
        style={{
          flexShrink: 0,
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${colors.divider}`,
          background: colors.background,
        }}
      >
        <button
          type="button"
          onClick={exit}
          data-testid="drafts-review-exit"
          aria-label="Exit drafts review"
          style={{
            justifySelf: 'start',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            padding: 8,
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke={colors.textPrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <span
          style={{
            justifySelf: 'center',
            fontSize: 14,
            fontWeight: 500,
            color: colors.textSecondary,
          }}
        >
          {remaining} remaining
        </span>
        <span
          aria-live="polite"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          Card {index} of {total}
        </span>
        <button
          type="button"
          onClick={() => setShowDeleteConfirm(true)}
          data-testid="drafts-review-delete"
          aria-label="Delete this thought"
          style={{
            justifySelf: 'end',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            padding: 8,
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke={colors.textSecondary} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Card body: fills the space between header and action bar. Internal
          scrolling is owned by the body region inside ThoughtDetail (cardMode),
          so the tags/AI suggestions/importance row stays anchored. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          padding: '12px 12px 0',
          width: '100%',
          maxWidth: 720,
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            // No outer card styling — the body floats on the page background
            // and the metadata pill below carries its own rounded chrome.
          }}
        >
          {activeThought && (
            // key forces a fresh ThoughtDetail per card so per-card
            // local state (e.g. permanentlyHideAiSuggestions) doesn't
            // leak from card N onto card N+1 and silently suppress
            // the prefetched suggestions we just warmed.
            <ThoughtDetail
              key={activeThought.id}
              thoughts={thoughts}
              allTags={allTags}
              thoughtId={activeThought.id}
              cardMode
              onSave={onSave}
              updateThoughtLocally={updateThoughtLocally}
              isDataLoading={false}
              deleteThought={() => {}}
            />
          )}
        </div>
      </div>

      {/* Action bar — pinned to the bottom edge of the viewport. */}
      <div
        style={{
          flexShrink: 0,
          padding: '16px 16px calc(16px + env(safe-area-inset-bottom))',
          background: colors.background,
          width: '100%',
          maxWidth: 720,
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="button"
            onClick={handleSkip}
            data-testid="drafts-review-skip"
            style={{
              flex: 1,
              padding: '14px 18px',
              borderRadius: 12,
              border: `1px solid ${colors.divider}`,
              background: 'transparent',
              color: colors.textSecondary,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Skip for now
          </button>
          <PrimaryButton
            onClick={handleDone}
            disabled={!canMarkDone}
            testId="drafts-review-done"
          >
            Mark as done
          </PrimaryButton>
        </div>
      </div>

      {showDeleteConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Confirm delete"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            style={{
              maxWidth: 360,
              width: '100%',
              background: colors.surfaceElevated,
              border: `1px solid ${colors.divider}`,
              borderRadius: 16,
              padding: 24,
              textAlign: 'center',
            }}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: 18, color: colors.textPrimary }}>
              Delete this thought?
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: 14, color: colors.textSecondary }}>
              This can't be undone.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                data-testid="drafts-review-delete-cancel"
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: 10,
                  border: `1px solid ${colors.divider}`,
                  background: 'transparent',
                  color: colors.textSecondary,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (activeThought && deleteThought) deleteThought(activeThought.id);
                  setShowDeleteConfirm(false);
                }}
                data-testid="drafts-review-delete-confirm"
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: 'rgb(230, 80, 71)',
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftsReview;
